import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { markSiteUnsynced } from "../../../utils/syncStore";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { UnitScanApplyResponse } from "./onvifScanInterfaces";
import { useOnvifScanStore } from "../onvifScanStore/onvifScanStore";
import { buildUnitScanApplyBody } from "./cameraUnitScan";

interface ApplyCameraUnitScanArgs {
  taskId: string;
  siteId: string;
  selectedIds: Record<number, boolean>;
  navigate: NavigateFunction;
  /** Called after a successful apply instead of navigating to Site About. */
  onSuccess?: () => void;
}

/**
 * POST camera-unit-scan/{task_id}/apply/ with the selected camera ids. On
 * success, mark the site unsynced when the backend says the change reached
 * connector settings, keep the updated/skipped lists for display, and go back
 * to Site About.
 */
export const applyCameraUnitScan = ({
  taskId,
  siteId,
  selectedIds,
  navigate,
  onSuccess,
}: ApplyCameraUnitScanArgs): Promise<UnitScanApplyResponse | null> => {
  const { setApplying, setApplyResult, setErrorMessage } =
    useOnvifScanStore.getState();

  const body = buildUnitScanApplyBody(selectedIds);
  if (body.camera_ids.length === 0) {
    return Promise.resolve(null);
  }

  setApplying(true);
  setErrorMessage("");

  const AxiosUI = createApi("");
  const retryFunction = () =>
    applyCameraUnitScan({ taskId, siteId, selectedIds, navigate, onSuccess });

  return AxiosUI.post(`camera-unit-scan/${taskId}/apply/`, body).then(
    (response: AxiosResponse<UnitScanApplyResponse>) => {
      const data = response.data;
      setApplyResult(data);
      if (data?.site_unsynced) {
        markSiteUnsynced(siteId);
      }
      setApplying(false);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/sites/aboutsite/${siteId}`);
      }
      return data;
    },
    (reason: AxiosError) => {
      setApplying(false);
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        const data = reason.response?.data as { error?: string } | undefined;
        setErrorMessage(
          data?.error || "Failed to apply the changes. Please try again."
        );
      });
      return null;
    }
  );
};
